import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import useHttpError from '../../../hooks/useHttpError/useHttpError';
import useSnackBar from '../../../hooks/useSnackBar/useSnackBar';
import useModal from '../../../hooks/useModal/useModal';
import { UpdateUserRequest, UserRequest, UserResponse } from '../../../models/UsersModels';
import UsersService from '../../../services/Users/UsersService';
import CompanyOnboardingService from '../../../services/CompanyOnboarding/CompanyOnboardingService';
import { setCompanyConfigs } from '../../../stores/UserDetailsStore';
import { setUserOperations } from '../../../stores/ESimManagerStore';

function useUserDetails(id: string | undefined) {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const snackBar = useSnackBar();
  const [t] = useTranslation();
  const { getHttpReasonFromList } = useHttpError();
  const modal = useModal();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /**
   * Fetches the user details by id
   * @param userId
   */
  const fetchUserDetails = (userId: string) => {
    setIsLoading(true);
    const requestParams: UserRequest = {
      id: userId,
    };
    UsersService.api.fetchUser(requestParams).then(
      (response) => {
        setUser(response.data);
        setIsLoading(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.userDetails.fetchUserError'), 'error');
      },
    );
  };

  /**
   * Fetches the current user operations
   */
  const fetchUserOperations = (userId: string) => {
    UsersService.api.fetchUserOperations({ id: userId }).then(
      (res) => {
        dispatch(setUserOperations(res.data.userOperations));
      },
      () => {
        snackBar.showSnackBar(t('pages.userDetails.fetchUserOperationsError'), 'error');
      },
    );
  };

  /**
   * Updates the user on the remote server
   * @param u
   * @param setFormOpen
   */
  const updateUserDetails = (u: UpdateUserRequest, setFormOpen: (val: boolean) => void) => {
    setIsLoading(true);
    UsersService.api.updateUser(u).then(
      (response) => {
        setIsLoading(false);
        setUser(response.data);
        setFormOpen(false);
        snackBar.showSnackBar(t('pages.userDetails.updateUserSuccess'), 'success');
      },
      (res) => {
        setIsLoading(false);
        const error = getHttpReasonFromList(
          res.response.data,
          [2068, 2077, 2078],
          t('pages.userDetails.updateUserError'),
        );
        snackBar.showSnackBar(error, 'error');
      },
    );
  };

  /**
   * Deletes the user
   * @param userId
   */
  const deleteUser = async (userId: string) => {
    setIsLoading(true);
    UsersService.api.deleteUser(userId).then(
      () => {
        navigate('/esimmanager');
        snackBar.showSnackBar(t('pages.userDetails.deleteUserSuccess'), 'success');
        setIsLoading(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.userDetails.deleteUserError'), 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Handler for when user presses the delete user
   * Checks if the schedulers contains the user
   * @param userId
   */
  const deleteUserHandler = async (userId: string) => {
    setIsLoading(true);
    UsersService.api.schedulerContainsUser(id as string).then(
      (res) => {
        if (res.data.hasSchedulers) {
          modal.showModal({
            modalTitle: t('pages.userDetails.warning'),
            modalDescription: (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <span>{t('pages.userDetails.scheduledWarningMessage1')}</span>
                <span>{t('pages.userDetails.scheduledWarningMessage2')}</span>
              </Box>
            ),
            confirmationModal: true,
            confirmationButtonLabel: t('common.delete'),
            onAccept: (setOpen) => {
              setOpen(false);
              deleteUser(userId);
            },
            onDiscard: () => {
              setIsLoading(false);
            },
          });
        } else {
          deleteUser(userId);
        }
      },
      () => {
        snackBar.showSnackBar(t('pages.userDetails.deleteUserError'), 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Fetches the company configs the user belongs to
   * @param ban
   */
  const getCompanyConfigs = (ban: string) => {
    CompanyOnboardingService.api.fetchCompanyConfigsByBan(ban).then(
      (res) => {
        dispatch(setCompanyConfigs(res.data));
      },
      () => {
        snackBar.showSnackBar(t('pages.eSimManager.newConnectionDrawer.fetchCompanyConfigsError'), 'error');
      },
    );
  };

  React.useEffect(() => {
    if (id) {
      fetchUserDetails(id);
      fetchUserOperations(id);
    }
  }, [id]);

  React.useEffect(() => {
    if (user) {
      getCompanyConfigs(user.billingAccountBan);
    }
  }, [user]);

  return { user, isLoading, updateUserDetails, deleteUserHandler };
}

export default useUserDetails;
